import { useLocalStorage } from "@mantine/hooks";
import type { TLCameraOptions } from "tldraw";

export type MouseTouchpadMode = "mouse" | "touchpad";

export const MOUSE_TOUCHPAD_STORAGE_KEY = "mouse-touchpad-mode";
export const TOUCHPAD_ZOOM_SPEED_STORAGE_KEY = "touchpad-zoom-speed";

export const DEFAULT_ZOOM_SPEED = 1;
export const MIN_ZOOM_SPEED = 0.1;
export const MAX_ZOOM_SPEED = 5.0;
export const ZOOM_SPEED_STEP = 0.1;

export function clampZoomSpeed(speed: number): number {
	if (typeof speed !== "number" || Number.isNaN(speed)) {
		return DEFAULT_ZOOM_SPEED;
	}
	const clamped = Math.min(MAX_ZOOM_SPEED, Math.max(MIN_ZOOM_SPEED, speed));
	return Number(clamped.toFixed(2));
}

export function getStoredZoomSpeed(): number {
	if (typeof window === "undefined") {
		return DEFAULT_ZOOM_SPEED;
	}
	try {
		const raw = window.localStorage.getItem(TOUCHPAD_ZOOM_SPEED_STORAGE_KEY);
		if (raw !== null) {
			const parsed = Number.parseFloat(raw);
			if (!Number.isNaN(parsed)) {
				return clampZoomSpeed(parsed);
			}
		}
	} catch {
		return DEFAULT_ZOOM_SPEED;
	}
	return DEFAULT_ZOOM_SPEED;
}

export function useMouseTouchpadStore() {
	const [mode, setMode] = useLocalStorage<MouseTouchpadMode | null>({
		key: MOUSE_TOUCHPAD_STORAGE_KEY,
		defaultValue: null,
		getInitialValueInEffect: false,
	});

	const [zoomSpeedRaw, setZoomSpeedRaw] = useLocalStorage<number>({
		key: TOUCHPAD_ZOOM_SPEED_STORAGE_KEY,
		defaultValue: DEFAULT_ZOOM_SPEED,
		getInitialValueInEffect: false,
		deserialize: (value) => {
			if (value === null || value === undefined) return DEFAULT_ZOOM_SPEED;
			try {
				const parsed = JSON.parse(value);
				const num =
					typeof parsed === "number"
						? parsed
						: Number.parseFloat(String(parsed));
				return Number.isNaN(num) ? DEFAULT_ZOOM_SPEED : clampZoomSpeed(num);
			} catch {
				const num = Number.parseFloat(value);
				return Number.isNaN(num) ? DEFAULT_ZOOM_SPEED : clampZoomSpeed(num);
			}
		},
	});

	const zoomSpeed =
		typeof zoomSpeedRaw === "number" && !Number.isNaN(zoomSpeedRaw)
			? clampZoomSpeed(zoomSpeedRaw)
			: DEFAULT_ZOOM_SPEED;

	const setZoomSpeed = (value: number | ((prev: number) => number)) => {
		if (typeof value === "function") {
			setZoomSpeedRaw((prev) =>
				clampZoomSpeed(
					value(
						typeof prev === "number" && !Number.isNaN(prev)
							? prev
							: DEFAULT_ZOOM_SPEED,
					),
				),
			);
		} else {
			setZoomSpeedRaw(clampZoomSpeed(value));
		}
	};

	const resetZoomSpeed = () => {
		setZoomSpeedRaw(DEFAULT_ZOOM_SPEED);
	};

	return {
		mode,
		setMode,
		zoomSpeed,
		setZoomSpeed,
		resetZoomSpeed,
	};
}

/**
 * Determines the navigation mode based on the wheel behavior setting.
 * Maps wheel behaviors to their corresponding navigation modes:
 * - "zoom" → "mouse" mode (scrolling zooms in/out)
 * - "pan" → "touchpad" mode (scrolling pans/moves canvas)
 * - "none" → defaults to "mouse" mode
 *
 * @param wheelBehavior - The current wheel behavior setting ("none" | "pan" | "zoom")
 * @returns The corresponding navigation mode ("mouse" | "touchpad")
 */
export function getBoardMode(
	wheelBehavior: TLCameraOptions["wheelBehavior"],
): MouseTouchpadMode {
	if (wheelBehavior === "zoom") {
		return "mouse";
	}
	if (wheelBehavior === "pan") {
		return "touchpad";
	}
	return "mouse";
}
